import React, { PropsWithChildren } from 'react';
import { classNames } from '../utils/classNames';

// A bordered frame that groups a few controls under a short caption. The caption sits in the top
// border line, like the legend of a form group, so the frame adds no row above its content.
// `fullWidth` makes the frame fill its parent (a narrow screen); by default it wraps its content.
type Props = {
	caption: string,
	icon?: React.ReactNode,
	fullWidth?: boolean,
	className?: string,
};

const CaptionGroup: React.FunctionComponent<PropsWithChildren<Props>> = ({
	caption,
	icon,
	fullWidth = false,
	className,
	children,
}) => <section aria-label={caption}
	className={classNames(
		'relative items-center rounded-md border border-gray-200 bg-white px-2 pb-2 pt-2.5 shadow-sm',
		fullWidth ? 'flex w-full' : 'inline-flex',
		className ?? '')}>
	<span className="absolute -top-2 left-2 inline-flex items-center gap-x-1 whitespace-nowrap bg-white px-1 text-xs font-normal leading-4 text-gray-500">
		{icon !== undefined && <span className="inline-flex shrink-0 text-blue-600" aria-hidden="true">{icon}</span>}
		{caption}
	</span>
	{children}
</section>;

// A thin vertical line between two sets of controls inside one CaptionGroup, for example two switches that each
// choose a different thing. Decoration only: a screen reader skips it. `className` can hide it on a narrow screen.
export const CaptionGroupDivider: React.FunctionComponent<{ className?: string }> = ({ className }) =>
	<span aria-hidden="true" className={classNames('mx-1 h-5 w-px shrink-0 self-center bg-gray-200', className ?? '')} />;

export default CaptionGroup;
